import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/pages/home/context/LangContext";

interface DemoModalProps {
  open: boolean;
  onClose: () => void;
}

const demoFeatures = [
  { icon: "ri-user-received-line", en: "Guest Check-In Flow", ar: "تدفق تسجيل الوصول" },
  { icon: "ri-shield-check-line", en: "Rasad Auto-Reporting", ar: "التقارير التلقائية لرصد" },
  { icon: "ri-bar-chart-box-line", en: "Live Analytics Dashboard", ar: "لوحة التحليلات المباشرة" },
  { icon: "ri-car-line", en: "Car Rental Management", ar: "إدارة تأجير السيارات" },
  { icon: "ri-passport-line", en: "Guided ID & Passport Entry", ar: "إدخال موجَّه للهوية والجواز" },
  { icon: "ri-building-4-line", en: "Multi-branch Control", ar: "التحكم متعدد الفروع" },
];

export default function DemoModal({ open, onClose }: DemoModalProps) {
  const { isAr, t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) {
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-3xl bg-ink rounded-3xl overflow-hidden border border-gold/20"
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
          <div>
            <h3 className={`font-bold text-xl text-white ${isAr ? "font-tajawal" : "font-inter"}`}>
              {t("See Diyafa in Action", "شاهد ضيافة في العمل")}
            </h3>
            <p className="text-sm text-white/50 mt-0.5">
              {t("A quick walkthrough of the platform", "جولة سريعة في المنصة")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white cursor-pointer transition-all"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Video placeholder */}
        <div className="relative bg-[#0F1628] mx-8 mt-6 rounded-2xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4A843' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            ></div>
            <div className="relative z-10 flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gold/20 border-2 border-gold/40 flex items-center justify-center">
                <i className="ri-play-fill text-gold text-4xl ml-1"></i>
              </div>
              <div className="text-center">
                <p className={`text-white font-semibold text-lg ${isAr ? "font-tajawal" : "font-inter"}`}>
                  {t("Product Demo Video", "فيديو عرض المنتج")}
                </p>
                <p className="text-white/40 text-sm mt-1">
                  {t("3 min overview • No signup required", "نظرة عامة 3 دقائق • لا يلزم التسجيل")}
                </p>
              </div>
            </div>
          </div>
          {/* Fake dashboard preview bars */}
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0F1628] to-transparent"></div>
        </div>

        {/* Feature highlights */}
        <div className="px-8 py-6">
          <p className={`text-xs font-semibold text-gold uppercase tracking-wider mb-4 ${isAr ? "font-tajawal text-right" : ""}`}>
            {t("What you'll see in the demo", "ما ستراه في العرض")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {demoFeatures.map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 p-3 rounded-xl bg-white/5 border border-white/8">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gold/15 flex-shrink-0">
                  <i className={`${f.icon} text-gold text-sm`}></i>
                </div>
                <span className={`text-xs text-white/70 ${isAr ? "font-tajawal" : ""}`}>
                  {isAr ? f.ar : f.en}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="px-8 pb-8 flex flex-col sm:flex-row gap-3">
          <button onClick={() => { onClose(); navigate("/register"); }} className={`flex-1 py-3 rounded-xl font-semibold text-ink bg-gold hover:bg-gold-dark cursor-pointer transition-all whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}>
            {t("Start Free Trial", "ابدأ مجاناً")}
          </button>
          <button
            onClick={onClose}
            className={`flex-1 py-3 rounded-xl font-semibold text-white/70 border border-white/15 hover:bg-white/5 cursor-pointer transition-all whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}
          >
            {t("Close", "إغلاق")}
          </button>
        </div>
      </div>
    </div>
  );
}
